import React, { useState, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { Upload, FileText, CheckCircle, AlertTriangle, X, FileSpreadsheet, Download } from 'lucide-react';
import { downloadIngredientTemplate, downloadRecipeTemplate } from '../utils/csvTemplates';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const COLUMN_MAP = {
  ingredient: 'name', ingredient_name: 'name', item: 'name', item_name: 'name', product: 'name', name: 'name',
  cost: 'purchaseCost', price: 'purchaseCost', purchase_cost: 'purchaseCost', unit_cost: 'purchaseCost',
  quantity: 'purchaseQuantity', qty: 'purchaseQuantity', amount: 'purchaseQuantity', purchase_quantity: 'purchaseQuantity',
  unit: 'purchaseUnit', uom: 'purchaseUnit', purchase_unit: 'purchaseUnit',
  supplier: 'vendor', vendor: 'vendor', vendor_name: 'vendor',
  category: 'category', cat: 'category', type: 'category',
  yield: 'yieldPercent', yield_percent: 'yieldPercent', 'yield_%': 'yieldPercent', usable_yield: 'yieldPercent',
  notes: 'notes', note: 'notes', comment: 'notes', comments: 'notes',
};

export default function UploadCenter() {
  const { bulkAddIngredients } = useApp();
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);

  const processData = useCallback((rows) => {
    if (!rows || rows.length === 0) {
      setError('No data found in file.');
      return;
    }

    const ingredients = [];
    for (const row of rows) {
      const mapped = {};
      for (const [rawKey, value] of Object.entries(row)) {
        const normalizedKey = rawKey.trim().toLowerCase().replace(/\s+/g, '_');
        const mappedKey = COLUMN_MAP[normalizedKey];
        if (mappedKey) {
          mapped[mappedKey] = value;
        }
      }

      const name = String(mapped.name || '').trim();
      if (!name) continue;

      ingredients.push({
        name,
        vendor: String(mapped.vendor || '').trim(),
        category: String(mapped.category || 'Other').trim(),
        purchaseCost: Number(mapped.purchaseCost) || 0,
        purchaseQuantity: Number(mapped.purchaseQuantity) || 1,
        purchaseUnit: String(mapped.purchaseUnit || 'each').trim().toLowerCase(),
        yieldPercent: Number(mapped.yieldPercent) || 100,
        notes: String(mapped.notes || '').trim(),
      });
    }

    if (ingredients.length === 0) {
      setError('No valid ingredients found. Make sure your file has a "Name" or "Ingredient" column.');
      return;
    }

    setPreview(ingredients);
    setError(null);
  }, []);

  const handleFile = useCallback((file) => {
    setResult(null);
    setError(null);
    setPreview(null);

    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => processData(results.data),
        error: (err) => setError(`CSV parse error: ${err.message}`),
      });
    } else if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const workbook = XLSX.read(e.target.result, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const data = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
        processData(data);
      };
      reader.onerror = () => setError('Error reading file.');
      reader.readAsArrayBuffer(file);
    } else {
      setError('Unsupported file format. Please use CSV or XLSX.');
    }
  }, [processData]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
  };

  const confirmImport = () => {
    if (!preview) return;
    bulkAddIngredients(preview);
    setResult({ count: preview.length });
    setPreview(null);
  };

  return (
    <div className="page-enter p-4 md:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-[#2E7D32] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]">
          <Upload className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-4xl sm:text-5xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="upload-title">
            Upload Center
          </h1>
          <p className="text-sm text-[#2A2A2A]/60">Import your ingredient list from CSV or XLSX.</p>
        </div>
      </div>

      {/* Drop Zone */}
      <div
        className={`pizza-card p-12 text-center border-dashed cursor-pointer transition-colors ${
          dragOver ? 'bg-[#FFC107]/10 border-[#FFC107]' : ''
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-input').click()}
        data-testid="upload-dropzone"
      >
        <input
          id="file-input"
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileInput}
          className="hidden"
          data-testid="upload-file-input"
        />
        <FileSpreadsheet className="w-16 h-16 text-[#2A2A2A]/20 mx-auto mb-4" />
        <h3 className="text-2xl text-[#2A2A2A] mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>
          Drop Your File Here
        </h3>
        <p className="text-sm text-[#2A2A2A]/50 mb-4">
          or click to browse. Supports CSV and XLSX.
        </p>
        <p className="text-xs text-[#2A2A2A]/30">
          Required column: Name (or Ingredient). Optional: Cost, Quantity, Unit, Vendor, Category, Yield %, Notes
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="mt-4 p-4 bg-[#E23D28]/10 border-2 border-[#E23D28] rounded-xl flex items-start gap-3" data-testid="upload-error">
          <AlertTriangle className="w-5 h-5 text-[#E23D28] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-[#E23D28] text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="mt-6" data-testid="upload-preview">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-2xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Preview ({preview.length} ingredients)
            </h2>
            <div className="flex gap-2">
              <button onClick={confirmImport} className="pizza-btn text-sm" data-testid="upload-confirm-btn">
                <CheckCircle className="w-4 h-4 inline mr-1" /> Import All
              </button>
              <button onClick={() => setPreview(null)} className="pizza-btn-secondary text-sm" data-testid="upload-cancel-btn">
                <X className="w-4 h-4 inline mr-1" /> Cancel
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="pizza-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Vendor</th>
                  <th>Category</th>
                  <th>Cost</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Yield %</th>
                </tr>
              </thead>
              <tbody>
                {preview.slice(0, 20).map((ing, i) => (
                  <tr key={i}>
                    <td className="font-medium">{ing.name}</td>
                    <td className="text-sm text-[#2A2A2A]/60">{ing.vendor || '-'}</td>
                    <td><span className="pizza-tag pizza-tag-yellow text-xs">{ing.category}</span></td>
                    <td>${ing.purchaseCost.toFixed(2)}</td>
                    <td>{ing.purchaseQuantity}</td>
                    <td>{ing.purchaseUnit}</td>
                    <td>{ing.yieldPercent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.length > 20 && (
              <p className="text-center text-sm text-[#2A2A2A]/40 mt-2">
                Showing first 20 of {preview.length} ingredients
              </p>
            )}
          </div>
        </div>
      )}

      {/* Success */}
      {result && (
        <div className="mt-4 p-4 bg-[#2E7D32]/10 border-2 border-[#2E7D32] rounded-xl flex items-start gap-3" data-testid="upload-success">
          <CheckCircle className="w-5 h-5 text-[#2E7D32] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-[#2E7D32] text-sm">
              Successfully imported {result.count} ingredients!
            </p>
            <p className="text-xs text-[#2A2A2A]/50 mt-1">
              Head to the Ingredient Database to review and edit.
            </p>
          </div>
        </div>
      )}

      {/* Template section */}
      <div className="mt-8 pizza-card p-6" data-testid="upload-template-info">
        <h2 className="text-2xl text-[#2A2A2A] mb-3" style={{ fontFamily: "'Bebas Neue', cursive" }}>
          File Format Guide
        </h2>
        <p className="text-sm text-[#2A2A2A]/60 mb-4">
          Your CSV or XLSX should have columns like these. Only "Name" is required.
        </p>
        <div className="overflow-x-auto">
          <table className="pizza-table text-sm">
            <thead>
              <tr>
                <th>Name*</th>
                <th>Vendor</th>
                <th>Category</th>
                <th>Cost</th>
                <th>Quantity</th>
                <th>Unit</th>
                <th>Yield %</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Mozzarella</td>
                <td>Sysco</td>
                <td>Dairy</td>
                <td>24.50</td>
                <td>5</td>
                <td>lb</td>
                <td>95</td>
                <td>Whole milk</td>
              </tr>
              <tr>
                <td>Pepperoni</td>
                <td>US Foods</td>
                <td>Meat</td>
                <td>18.00</td>
                <td>3</td>
                <td>lb</td>
                <td>100</td>
                <td>Sliced</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={downloadIngredientTemplate}
            className="pizza-btn text-xs px-4 py-2 flex items-center gap-1" data-testid="upload-download-ing-template">
            <Download className="w-3.5 h-3.5" /> Download Ingredient Template
          </button>
          <button onClick={downloadRecipeTemplate}
            className="pizza-btn-secondary text-xs px-4 py-2 flex items-center gap-1" data-testid="upload-download-recipe-template">
            <Download className="w-3.5 h-3.5" /> Download Recipe Template
          </button>
        </div>
      </div>
    </div>
  );
}
